/* ============================================================
   Petite Studio custom reviews — site-wide card badge loader.

   Fills every [data-ps-review-badge] element with "XX reviews"
   from the reviews API summary endpoint.

   Rules (per project contract):
   - Lazy: fetch only when the card nears the viewport.
   - One fetch per product id per page view (in-page cache),
     shared across grid / carousel / recently-viewed duplicates.
   - Fail silent: any error, non-200, or zero reviews = render
     nothing. Never throw, never show an error to the customer.
   - Plain GET fetch. No credentials, no custom headers (CORS).
   - Picks up cards injected after page load (infinite scroll,
     quickview, recently-viewed) via a MutationObserver.
   ============================================================ */

(function () {
  'use strict';

  var API_BASE = 'https://petite-studio-app.vercel.app/api/public';
  var BADGE_SELECTOR = '[data-ps-review-badge]';

  /* In-page cache: product id -> Promise resolving to summary or null */
  var summaryCache = {};

  function fetchSummary(productId) {
    if (summaryCache[productId]) return summaryCache[productId];

    summaryCache[productId] = fetch(
      API_BASE + '/reviews/summary?product_id=' + productId
    )
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () {
        return null;
      });

    return summaryCache[productId];
  }

  /* Flickity carousels (best sellers etc.) measure their height at
     init. Badge text arriving later grows the card and the price can
     get clipped. Ask each Flickity instance directly to re-measure —
     NEVER a global window resize event, which wakes every resize
     listener in the theme and causes side effects. */
  var resizeTimer = null;
  function nudgeCarousels() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      try {
        if (!window.Flickity || typeof window.Flickity.data !== 'function') return;
        var carousels = document.querySelectorAll('.flickity-enabled');
        for (var i = 0; i < carousels.length; i++) {
          var instance = window.Flickity.data(carousels[i]);
          if (instance && typeof instance.resize === 'function') instance.resize();
        }
      } catch (e) {
        /* fail silent */
      }
    }, 150);
  }

  function renderBadge(el, summary) {
    if (!summary || !summary.count || summary.count < 1) return;
    var label = summary.count === 1 ? '1 review' : summary.count + ' reviews';
    el.textContent = label;
    nudgeCarousels();
  }

  function loadBadge(el) {
    var productId = el.getAttribute('data-ps-review-badge') || '';
    if (!/^\d+$/.test(productId)) return;
    fetchSummary(productId).then(function (summary) {
      try {
        renderBadge(el, summary);
      } catch (e) {
        /* fail silent */
      }
    });
  }

  /* ============================================================
     Top widget (product page near title/price, and Quick View):
     stars + count (stars hidden when average < 4.0), preview
     review thumbnail + ~90 chars of body. Click = scroll to the
     main reviews section, or navigate to it from Quick View.
     ============================================================ */

  var firstPageCache = {};

  function fetchFirstPage(productId) {
    if (firstPageCache[productId]) return firstPageCache[productId];
    firstPageCache[productId] = fetch(
      API_BASE + '/reviews?product_id=' + productId + '&per_page=50&page=1'
    )
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () {
        return null;
      });
    return firstPageCache[productId];
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function starsNode(rating) {
    var wrap = el('span', 'ps-stars');
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', rating + ' out of 5 stars');
    var bg = el('span', 'ps-stars__bg', '★★★★★');
    bg.setAttribute('aria-hidden', 'true');
    var fg = el('span', 'ps-stars__fg', '★★★★★');
    fg.setAttribute('aria-hidden', 'true');
    fg.style.width = Math.max(0, Math.min(100, (rating / 5) * 100)) + '%';
    wrap.appendChild(bg);
    wrap.appendChild(fg);
    return wrap;
  }

  function truncateText(text, max) {
    if (!text || text.length <= max) return text || '';
    var cut = text.slice(0, max + 1);
    var space = cut.lastIndexOf(' ');
    if (space > 40) cut = cut.slice(0, space);
    else cut = text.slice(0, max);
    return cut.replace(/[\s.,;:!?]+$/, '') + '…';
  }

  function renderTopWidget(container, summary, page) {
    if (!summary || !summary.count || summary.count < 1) return;
    /* average may be null (only unrated influencer reviews) — still
       render count + preview; stars are already gated on >= 4.0 below. */
    var reviews = (page && page.reviews) || [];
    if (!reviews.length) return;

    var preview = reviews[0]; /* API order: pinned first, else newest */

    /* Thumbnail: first review in API order with an image (featured
       sort already puts pinned reviews first). If none, and the
       preview review has a video, show its frame + ghost play. */
    var thumbMedia = null;
    var thumbIsVideo = false;
    outer:
    for (var i = 0; i < reviews.length; i++) {
      var media = reviews[i].media || [];
      for (var m = 0; m < media.length; m++) {
        if (media[m] && media[m].url && media[m].media_type === 'image') {
          thumbMedia = media[m];
          break outer;
        }
      }
    }
    if (!thumbMedia) {
      var pm = preview.media || [];
      for (var v = 0; v < pm.length; v++) {
        if (pm[v] && pm[v].url && pm[v].media_type === 'video') {
          thumbMedia = pm[v];
          thumbIsVideo = true;
          break;
        }
      }
    }

    if (thumbMedia) {
      var thumb = el('span', 'ps-top-widget__thumb' + (thumbIsVideo ? ' ps-media--video' : ''));
      if (thumbIsVideo) {
        var video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.src = thumbMedia.url;
        video.setAttribute('tabindex', '-1');
        thumb.appendChild(video);
        thumb.appendChild(el('span', 'ps-media__play', '▶'));
      } else {
        var img = document.createElement('img');
        img.alt = 'Customer review photo';
        img.loading = 'lazy';
        img.src = thumbMedia.url;
        thumb.appendChild(img);
      }
      container.appendChild(thumb);
    }

    var content = el('span', 'ps-top-widget__content');
    var head = el('span', 'ps-top-widget__head');
    if (summary.average >= 4.0) head.appendChild(starsNode(summary.average));
    head.appendChild(
      el('span', 'ps-top-widget__count',
        summary.count === 1 ? '1 review' : summary.count + ' reviews')
    );
    content.appendChild(head);

    var previewText = truncateText(preview.body || '', 90);
    if (previewText) {
      content.appendChild(el('span', 'ps-top-widget__preview', previewText));
    }
    container.appendChild(content);

    container.setAttribute('role', 'button');
    container.setAttribute('tabindex', '0');
    container.setAttribute('aria-label', 'Read customer reviews');

    function goToReviews() {
      try {
        var target = document.getElementById('ps-reviews');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          var url = container.getAttribute('data-product-url');
          if (url) window.location.href = url + '#ps-reviews';
        }
      } catch (e) {
        /* fail silent */
      }
    }

    container.addEventListener('click', goToReviews);
    container.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        goToReviews();
      }
    });
  }

  function loadTopWidget(container) {
    var productId = container.getAttribute('data-product-id') || '';
    if (!/^\d+$/.test(productId)) return;
    Promise.all([fetchSummary(productId), fetchFirstPage(productId)])
      .then(function (results) {
        try {
          renderTopWidget(container, results[0], results[1]);
        } catch (e) {
          /* fail silent */
        }
      });
  }

  /* Lazy loading — fetch a little before the card scrolls into view */
  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          loadBadge(entry.target);
        });
      },
      { rootMargin: '200px 0px' }
    );
  }

  function watchBadges(root) {
    if (!root || !root.querySelectorAll) return;
    var badges = root.querySelectorAll(BADGE_SELECTOR);
    for (var i = 0; i < badges.length; i++) {
      var node = badges[i];
      if (node.hasAttribute('data-ps-observed')) continue;
      node.setAttribute('data-ps-observed', '1');
      if (observer) {
        observer.observe(node);
      } else {
        loadBadge(node); /* very old browser: load eagerly */
      }
    }

    /* Top widgets load eagerly — they sit near the top of the page
       (or inside a just-opened Quick View) */
    var tops = root.querySelectorAll('[data-ps-top-widget]');
    for (var t = 0; t < tops.length; t++) {
      var top = tops[t];
      if (top.hasAttribute('data-ps-observed')) continue;
      top.setAttribute('data-ps-observed', '1');
      loadTopWidget(top);
    }
  }

  function init() {
    try {
      watchBadges(document);

      /* Catch cards injected later: infinite scroll, quickview,
         recently-viewed, etc. */
      if ('MutationObserver' in window) {
        var mo = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            var added = mutations[i].addedNodes;
            for (var j = 0; j < added.length; j++) {
              if (added[j].nodeType === 1) watchBadges(added[j]);
            }
          }
        });
        mo.observe(document.body, { childList: true, subtree: true });
      }
    } catch (e) {
      /* fail silent */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
